import { useMemo } from "react";
import { type NavigationConfig } from "../components/layout/sidebarUtils";
import { useSalesCopilotModalStore } from "../store/salesCopilotModalStore";
import env from "../utils/env";
import {
  endImpersonation,
  isImpersonating,
  isImpersonatorSuperKudu,
} from "../utils/impersonation";
import { getSegmentIdFromSearch } from "../utils/companyRouteContext";
import { ADMIN_CONTEXT_ORG_ID_SESSION_KEY } from "../const";
import { getRbacClaims, getUserClaims } from "../utils/auth";
import { hasRole, isV2User, tenantPurchased } from "../utils/rbac";
import { useFeatureFlag } from "../hooks/useLaunchDarkly";
import {
  faGear,
  faFolder,
  faArrowLeft,
  faLayerGroup,
  faPuzzlePiece,
  faCircleQuestion,
  faCompass,
  faRectangleList,
  faSliders,
  faBookmark,
  faClockRotateLeft,
  faFileInvoiceDollar,
} from "@fortawesome/pro-regular-svg-icons";

export const useNavigationConfig = (): NavigationConfig => {
  const openSalesCopilotModal = useSalesCopilotModalStore(
    (state) => state.openModal
  );
  const scoringManagerEnabled = useFeatureFlag(
    "maScoringProfilesManager",
    false
  );
  return useMemo(() => {
    const claims = getUserClaims();
    // Export quotes are SuperKudu-only.
    const quotesEnabled = getRbacClaims().superKudu;
    if (isV2User(claims.rbac)) {
      return getV2NavigationConfig(scoringManagerEnabled, quotesEnabled);
    }
    return getGlobalNavigationConfig(
      openSalesCopilotModal,
      scoringManagerEnabled,
      quotesEnabled
    );
  }, [openSalesCopilotModal, scoringManagerEnabled]);
};

const quotesNavigationItem = {
  label: "Quotes",
  href: "/quotes",
  icon: faFileInvoiceDollar,
  id: "sidebar-link-quotes",
  description: "View saved export quotes",
  superKuduOnly: true,
  activeMatcher: (path: string) => path === "/quotes",
};

/**
 * Builds an org-less Admin link. Admin resolves the org from the logged-in
 * session, so the link can't point at a stale org after a logout or an
 * impersonation change (RGI-2658).
 */
const adminLink = (
  adminUrl: string | undefined,
  path: string,
  fallback: string
): string => (adminUrl ? `${adminUrl.replace(/\/$/, "")}${path}` : fallback);

const clearAdminContextOrgId = (): void => {
  try {
    sessionStorage.removeItem(ADMIN_CONTEXT_ORG_ID_SESSION_KEY);
  } catch {
    // Best effort only
  }
};

const getGlobalNavigationConfig = (
  openSalesCopilotModal: () => void,
  scoringManagerEnabled: boolean,
  quotesEnabled: boolean
): NavigationConfig => {
  const adminUrl = env.ADMIN_URL;

  // RBAC v2 cross-module gate (RGI-1042 / RGI-1113). Hide the "Open Sales
  // Copilot" PROSPECTING section when the org hasn't purchased sales_copilot.
  // v1 users (no `mkClaims.rbac` on JWT) and SuperKudu always see it.
  const claims = getUserClaims();
  const showCopilotPromo =
    !isV2User(claims.rbac) ||
    !!claims.superKudu ||
    tenantPurchased(claims.rbac, "sales_copilot");

  const settingsUrl = adminLink(adminUrl, "/settings/account", "#");
  const helpUrl = adminLink(adminUrl, "/help", "#");
  const backHomeUrl = adminLink(adminUrl, "/", "/projects");

  const hasSegmentIdParam = (search: string): boolean =>
    Boolean(getSegmentIdFromSearch(search));

  return {
    standaloneLinks: [
      {
        label: "Back Home",
        href: backHomeUrl,
        icon: faArrowLeft,
        description: "Return to home",
        id: "sidebar-link-home",
        activeMatcher: () => false, // Never highlight Back Home
        onClick: adminUrl
          ? () => {
              // Leaving Market Analyzer for Admin should not keep a stale
              // impersonated session around.
              if (isImpersonating() && isImpersonatorSuperKudu()) {
                endImpersonation();
              }
              clearAdminContextOrgId();
              window.location.href = backHomeUrl;
            }
          : undefined,
      },
    ],
    sections: [
      {
        label: "MARKET\u00A0ANALYZER",
        items: [
          {
            label: "Projects",
            href: "/projects",
            icon: faFolder,
            id: "sidebar-link-market-analyzer",
            description: "Create and manage your market analysis projects",
            activeMatcher: (path) =>
              path === "/projects" ||
              path.startsWith("/projects/") ||
              path === "/workspaces" ||
              path.startsWith("/workspaces/"),
          },
        ],
      },
      {
        label: "SEGMENTATION",
        items: [
          {
            label: "Companies",
            href: "/segments/explore",
            icon: faCompass,
            id: "sidebar-link-segments-companies",
            description: "Explore and filter companies to build segments",
            activeMatcher: (path: string, search: string) =>
              path === "/segments/explore" ||
              (path.startsWith("/catalog/companies/") &&
                !hasSegmentIdParam(search)),
          },
          {
            label: "Segments",
            href: "/segments",
            icon: faRectangleList,
            id: "sidebar-link-segments-list",
            description: "View and manage your saved segments",
            activeMatcher: (path: string, search: string) =>
              path === "/segments" ||
              (path.startsWith("/segments/") &&
                path !== "/segments/explore" &&
                path !== "/segments/scoring-profiles") ||
              (path.startsWith("/catalog/companies/") &&
                hasSegmentIdParam(search)),
          },
          ...(quotesEnabled ? [quotesNavigationItem] : []),
          ...(scoringManagerEnabled
            ? [
                {
                  label: "Scoring",
                  href: "/segments/scoring-profiles",
                  icon: faSliders,
                  id: "sidebar-link-scoring-profiles",
                  description: "Create and manage scoring profiles",
                  activeMatcher: (path: string) =>
                    path === "/segments/scoring-profiles",
                },
              ]
            : []),
        ],
      },
      {
        label: "CATALOG",
        items: [
          {
            label: "Tech & Taxonomies",
            href: "/catalog/product",
            icon: faLayerGroup,
            id: "sidebar-link-predictions",
            description:
              "Explore technology categories and taxonomies for market analysis.",
            activeMatcher: (path) => path.includes("/catalog/product"),
          },
          {
            label: "Saved Collections",
            href: "/settings/collections",
            icon: faBookmark,
            id: "sidebar-link-saved-collections",
            description: "Manage your saved collections",
            activeMatcher: (path: string) =>
              path.startsWith("/settings/collections"),
          },
          {
            label: "Export History",
            href: "/settings/exports",
            icon: faClockRotateLeft,
            id: "sidebar-link-export-history",
            description: "Review your past export jobs and downloads.",
            activeMatcher: (path: string) =>
              path.startsWith("/settings/exports"),
          },
        ],
      },
      ...(showCopilotPromo
        ? [
            {
              label: "PROSPECTING",
              items: [
                {
                  label: "Open Sales Copilot",
                  href: "#",
                  icon: faPuzzlePiece,
                  description: "Access the Sales Copilot application",
                  onClick: openSalesCopilotModal,
                  variant: "highlight" as const,
                  id: "sidebar-button-open-sales-copilot",
                },
              ],
            },
          ]
        : []),
    ],
    bottomLinks: [
      {
        label: "Settings",
        href: settingsUrl,
        icon: faGear,
        id: "sidebar-link-settings",
        description: "Configure account and organization settings",
        activeMatcher: () => false, // Settings is always external, so never active
        onClick:
          settingsUrl !== "#"
            ? () => {
                window.location.href = settingsUrl;
              }
            : undefined,
      },
      {
        label: "Help & Support",
        href: helpUrl,
        icon: faCircleQuestion,
        id: "sidebar-link-help",
        description: "Get help and contact support",
        activeMatcher: () => false, // Help is always external, so never active
        onClick:
          helpUrl !== "#"
            ? () => {
                window.location.href = helpUrl;
              }
            : undefined,
      },
    ],
  };
};

/**
 * Unified single-sidebar nav for v2 RBAC users.
 *
 * Trimmed vs. the v1 config: no "Back Home" standalone (the v2 app switcher
 * will replace cross-module nav), no PROSPECTING / Open Sales Copilot promo.
 * Settings survives as "Admin Center" — a second entry point into admin next
 * to the app switcher, same destination as v1's Settings (RGI-2609).
 * MARKET ANALYZER section label is dropped so Projects becomes standalone.
 */
const getV2NavigationConfig = (
  scoringManagerEnabled: boolean,
  quotesEnabled: boolean
): NavigationConfig => {
  const adminUrl = env.ADMIN_URL;

  const helpUrl = adminLink(adminUrl, "/help", "#");
  const adminCenterUrl = adminLink(adminUrl, "/settings/account", "#");

  // Only org admins can land in admin, so a standard user gets no link there
  // (RGI-2664). SuperKudu keeps it; the switcher's Admin Hub entry is gated
  // the same way via canSeeAdminHub.
  const claims = getUserClaims();
  const showAdminCenter = hasRole(claims.rbac, "orgAdmin", {
    superKudu: !!claims.superKudu,
  });

  const hasSegmentIdParam = (search: string): boolean =>
    Boolean(getSegmentIdFromSearch(search));

  return {
    sections: [
      {
        items: [
          {
            label: "Projects",
            href: "/projects",
            icon: faFolder,
            id: "sidebar-link-market-analyzer",
            description: "Create and manage your market analysis projects",
            activeMatcher: (path) =>
              path === "/projects" ||
              path.startsWith("/projects/") ||
              path === "/workspaces" ||
              path.startsWith("/workspaces/"),
          },
        ],
      },
      {
        label: "SEGMENTATION",
        items: [
          {
            label: "Companies",
            href: "/segments/explore",
            icon: faCompass,
            id: "sidebar-link-segments-companies",
            description: "Explore and filter companies to build segments",
            activeMatcher: (path: string, search: string) =>
              path === "/segments/explore" ||
              (path.startsWith("/catalog/companies/") &&
                !hasSegmentIdParam(search)),
          },
          {
            label: "Segments",
            href: "/segments",
            icon: faRectangleList,
            id: "sidebar-link-segments-list",
            description: "View and manage your saved segments",
            activeMatcher: (path: string, search: string) =>
              path === "/segments" ||
              (path.startsWith("/segments/") &&
                path !== "/segments/explore" &&
                path !== "/segments/scoring-profiles") ||
              (path.startsWith("/catalog/companies/") &&
                hasSegmentIdParam(search)),
          },
          ...(quotesEnabled ? [quotesNavigationItem] : []),
          ...(scoringManagerEnabled
            ? [
                {
                  label: "Scoring",
                  href: "/segments/scoring-profiles",
                  icon: faSliders,
                  id: "sidebar-link-scoring-profiles",
                  description: "Create and manage scoring profiles",
                  activeMatcher: (path: string) =>
                    path === "/segments/scoring-profiles",
                },
              ]
            : []),
        ],
      },
      {
        label: "CATALOG",
        items: [
          {
            label: "Tech & Taxonomies",
            href: "/catalog/product",
            icon: faLayerGroup,
            id: "sidebar-link-predictions",
            description:
              "Explore technology categories and taxonomies for market analysis.",
            activeMatcher: (path) => path.includes("/catalog/product"),
          },
          {
            label: "Saved collection",
            href: "/settings/collections",
            icon: faBookmark,
            id: "sidebar-link-saved-collections",
            description: "Manage your saved collections",
            activeMatcher: (path: string) =>
              path.startsWith("/settings/collections"),
          },
          {
            label: "Export History",
            href: "/settings/exports",
            icon: faClockRotateLeft,
            id: "sidebar-link-export-history",
            description: "Review your past export jobs and downloads.",
            activeMatcher: (path: string) =>
              path.startsWith("/settings/exports"),
          },
        ],
      },
    ],
    bottomLinks: [
      ...(showAdminCenter
        ? [
            {
              label: "Admin Center",
              href: adminCenterUrl,
              icon: faGear,
              id: "sidebar-link-admin-center",
              description: "Manage your organization's settings and users",
              activeMatcher: () => false,
              onClick:
                adminCenterUrl !== "#"
                  ? () => {
                      window.location.href = adminCenterUrl;
                    }
                  : undefined,
            },
          ]
        : []),
      {
        label: "Help & Support",
        href: helpUrl,
        icon: faCircleQuestion,
        id: "sidebar-link-help",
        description: "Get help and contact support",
        activeMatcher: () => false,
        onClick:
          helpUrl !== "#"
            ? () => {
                window.location.href = helpUrl;
              }
            : undefined,
      },
    ],
  };
};
